import HTTPError from 'ext/lib/api/http-error';

/**
 * Resolves a token from either a static string or a token provider function.
 * @param {string|function} tokenOrProvider - Static token or async function returning token
 * @returns {Promise<string>} The resolved token
 */
const resolveToken = async tokenOrProvider => {
  if (typeof tokenOrProvider === 'function') {
    return tokenOrProvider();
  }
  return tokenOrProvider;
};

/**
 * Creates a fetch function for API calls.
 * @param {string} baseUrl - The base URL for the API
 * @param {object} auth - Authentication config
 * @param {string} auth.accountId - The account ID
 * @param {string|function} auth.token - Static token string or async function that returns a fresh token
 */
export const createFetch =
  (baseUrl, { accountId, token }) =>
  async (path, options = {}, root = `/accounts/${accountId}/`) => {
    const {
      contentType = 'application/json',
      parse = true,
      ignoreContentType = false,
      headers = {},
      responseType,
    } = options;

    // Resolve token - supports both static tokens and token provider functions
    const resolvedToken = await resolveToken(token);

    const response = await fetch(`${baseUrl}${root}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...(ignoreContentType ? {} : { 'Content-Type': contentType }),
        Authorization: `Bearer ${resolvedToken}`,
      },
    });

    if (!response.ok) {
      // A gateway's 401 may have no JSON body; keep the status regardless
      const error = await response.json().catch(() => null);
      throw Object.assign(new HTTPError(JSON.stringify(error)), {
        status: response.status,
      });
    }

    if (responseType === 'blob') {
      return response.blob();
    }

    const text = await response.text();

    return text && text.length > 0 ? (parse ? JSON.parse(text) : text) : null;
  };
